<template>
    <div class="folder-picker">
        <div v-if="selected.length" class="folder-picker-chips">
            <span v-for="f in selected" :key="f.slug" class="folder-picker-chip" :title="f.path">
                <svg-icon icon="folder" />{{ f.name }}<span v-if="f.parent" class="folder-picker-in">in {{ f.parent }}</span>
                <svg-icon icon="x" title="Remove" @click="remove(f.slug)" />
            </span>
        </div>
        <div v-if="loadError" class="folder-picker-empty">Couldn't load folders. You can file it from the Folders page later.</div>
        <template v-else-if="loaded">
            <input type="text" v-model="query" placeholder="Search folders..." @focus="open = true" @keydown.esc="open = false" @keydown.enter.prevent="pickFirst()" />
            <div v-if="open || query" class="folder-picker-list">
                <div v-for="f in matches" :key="f.slug" class="folder-picker-option" @mousedown.prevent="add(f.slug)">
                    <span class="folder-picker-name">{{ f.name }}</span>
                    <span v-if="f.parent" class="folder-picker-path">{{ f.parent }}</span>
                </div>
                <div v-if="!matches.length" class="folder-picker-empty">{{ query ? 'No folders match "' + query + '"' : 'Every folder is already picked' }}</div>
            </div>
        </template>
        <div v-else class="folder-picker-empty">Loading folders...</div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import SvgIcon from './SvgIcon.vue';

// Picks the folders (NODE-type playbook nodes, plus Home) a new node is filed under.
// v-model is an array of folder slugs; each becomes a tblNextNode link when the node is created.
// Folders are labelled with where they sit (first path from Home) so same-named folders are told apart.
export default defineComponent({
    name: 'FolderPicker',
    props: {
        modelValue: { type: Array, default: () => [] }
    },
    emits: ['update:modelValue'],
    components: { SvgIcon },
    data() {
        return {
            folders: [] as any[], // { slug, name, parent, path }; path is what search matches
            loaded: false,
            loadError: false,
            query: '',
            open: false
        }
    },
    computed: {
        selected(): any[] {
            return (this.modelValue as string[]).map(slug => this.folders.find(f => f.slug == slug)).filter(Boolean);
        },
        matches(): any[] {
            const q = this.query.trim().toLowerCase();
            const picked = this.modelValue as string[];
            return this.folders
                .filter(f => !picked.includes(f.slug) && (!q || f.path.toLowerCase().includes(q)))
                .slice(0, 50);
        }
    },
    async created() {
        document.addEventListener('mousedown', this.onOutside);
        await axios.get(`${this.$store.state.apiUrl}/api/playbook/foldertree`)
            .then(resp => {
                this.folders = this.buildFolders(resp.data.nodes, resp.data.links);
                // Drop any preselected slug that isn't a folder (e.g. opened from a page that isn't one)
                const valid = (this.modelValue as string[]).filter(slug => this.folders.some(f => f.slug == slug));
                if (valid.length != (this.modelValue as string[]).length) this.$emit('update:modelValue', valid);
                this.loaded = true;
            })
            .catch(e => {
                console.log(e?.response?.data || e);
                this.loadError = true;
            });
    },
    unmounted() {
        document.removeEventListener('mousedown', this.onOutside);
    },
    methods: {
        buildFolders(nodes: any[], links: any[]): any[] {
            const folderMap: any = {};
            for (const n of nodes) if (n.Type == 'NODE') folderMap[n.Slug] = n;
            const children: any = {};
            for (const l of links) {
                if (!folderMap[l.NextSlug] || (l.ThisSlug != 'home' && !folderMap[l.ThisSlug])) continue;
                (children[l.ThisSlug] = children[l.ThisSlug] || []).push(l.NextSlug);
            }
            // Breadth-first gives each folder its shortest path: from Home first, then from each
            // folder nothing links to, so folders outside the main tree still show where they sit
            const trail: any = {};
            const walk = (root: string, start: string[]) => {
                trail[root] = start;
                const queue = [root];
                while (queue.length) {
                    const slug = queue.shift() as string;
                    for (const child of children[slug] || []) {
                        if (trail[child]) continue;
                        trail[child] = [...trail[slug], folderMap[child].Name];
                        queue.push(child);
                    }
                }
            };
            walk('home', []);
            const inTree = new Set(Object.keys(trail));
            const linked = new Set(links.map((l: any) => l.NextSlug));
            for (const slug of Object.keys(folderMap)) if (!trail[slug] && !linked.has(slug)) walk(slug, [folderMap[slug].Name]);
            for (const slug of Object.keys(folderMap)) if (!trail[slug]) walk(slug, [folderMap[slug].Name]); // loops
            // Home is a node too; it's listed once, first, under its own name
            const homeName = (folderMap.home && folderMap.home.Name) || 'Home';
            const list = Object.values(folderMap).filter((n: any) => n.Slug != 'home').map((n: any) => {
                const names: string[] = trail[n.Slug];
                const parent = names.length > 1 ? names.slice(0, -1).join(' › ') : inTree.has(n.Slug) ? homeName : 'Not in any folder';
                return { slug: n.Slug, name: n.Name, parent, path: names.join(' › '), inTree: inTree.has(n.Slug) };
            });
            // The main tree first, then folders outside it
            list.sort((a: any, b: any) => (a.inTree === b.inTree ? 0 : a.inTree ? -1 : 1) || a.path.localeCompare(b.path));
            return [{ slug: 'home', name: homeName, parent: 'Top level', path: homeName, inTree: true }, ...list];
        },
        add(slug: string) {
            this.$emit('update:modelValue', [...(this.modelValue as string[]), slug]);
            this.query = '';
        },
        remove(slug: string) {
            this.$emit('update:modelValue', (this.modelValue as string[]).filter(s => s != slug));
        },
        pickFirst() {
            if (this.matches.length) this.add(this.matches[0].slug);
        },
        onOutside(e: any) {
            if (!this.$el.contains(e.target)) this.open = false;
        }
    }
});
</script>

<style scoped>
.folder-picker { position: relative; }
.folder-picker input[type=text] { margin: 0; width: 100%; box-sizing: border-box; }
.folder-picker-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.folder-picker-chip { display: inline-flex; align-items: center; gap: 5px; padding: 4px 6px 4px 9px; border-radius: 14px; background: var(--primary-color); color: #fff; font-size: 13px; max-width: 100%; }
.folder-picker-chip svg { width: 14px; height: 14px; fill: currentColor; flex: none; }
.folder-picker-chip svg:last-child { cursor: pointer; }
.folder-picker-in { opacity: .75; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.folder-picker-list { margin-top: 4px; max-height: 220px; overflow-y: auto; background: #fff; border: 1px solid #ead8c8; border-radius: 10px; box-shadow: 0 8px 24px rgba(70, 35, 10, .10); }
.folder-picker-option { display: flex; flex-direction: column; padding: 8px 12px; cursor: pointer; border-bottom: 1px solid #f6ece3; }
.folder-picker-option:last-child { border-bottom: none; }
.folder-picker-option:hover { background: #fff5ec; }
.folder-picker-name { font-size: 14px; color: #1f1712; font-weight: 600; }
.folder-picker-path { font-size: 12px; color: #8a7f75; }
.folder-picker-empty { padding: 8px 12px; font-size: 13px; color: #8a7f75; }
</style>
